/**
 * Which module's cycle Management ▸ Cycles ▸ Findings reads — `?module=`.
 *
 * Issue 226 (giro 3 «Dopo» point 3, owner ruling 2026-10-07: the MOVE). The exit cycle's findings
 * left Compacting ▸ Consolidation ▸ Estate for this page, beside the in-season ones: the cycle is a
 * Management concern for every module. A «Module» selector (In season · Exit) in the run-choice
 * band decides which kind of run the page chooses, and the choice is the URL (G2.2):
 *
 * - absent, empty, `inseason` or anything this build does not know: the in-season run's findings,
 *   exactly as before the move — every link made before it opens unchanged;
 * - `exit`: `?runId=` names a consolidation decision run (`exit.consolidation_run`), and the page
 *   draws what the exit cycle found.
 *
 * The parameter is not the run: changing module drops `?runId=`, because a run of one kind is not
 * a run of the other, and the DEC-99 default (ADR 9039) then opens the newest run of
 * the new kind. Pure parse, so a test and an export resolver read a string.
 */

import { navigateToScope, RUN_ID_PARAM } from './scope';

export const CYCLE_MODULE_PARAM = 'module';

export type CycleModuleChoice = 'inseason' | 'exit';

/** The modules in the selector's order. */
export const CYCLE_MODULE_CHOICES: readonly CycleModuleChoice[] = ['inseason', 'exit'];

/** The module a search string names; anything but `exit` is the in-season module. Pure. */
export function readCycleModule(search: string): CycleModuleChoice {
  return new URLSearchParams(search).get(CYCLE_MODULE_PARAM)?.trim() === 'exit' ? 'exit' : 'inseason';
}

/**
 * The search a planner's module choice navigates to: `?module=exit`, or no `module` at all for the
 * in-season default (the link the page has always had), with `?runId=` dropped. Pure.
 */
export function cycleModuleSearch(search: string, module: CycleModuleChoice): URLSearchParams {
  const next = new URLSearchParams(search);
  next.delete(RUN_ID_PARAM);
  if (module === 'exit') next.set(CYCLE_MODULE_PARAM, 'exit');
  else next.delete(CYCLE_MODULE_PARAM);
  return next;
}

/** The planner chose a module: a history step, so Back returns to the module they left. */
export function chooseCycleModule(module: CycleModuleChoice): void {
  if (typeof window === 'undefined') return;
  navigateToScope(cycleModuleSearch(window.location.search, module));
}
